import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  CheckCircle2, 
  Plane, 
  Calendar, 
  MapPin, 
  Key, 
  HelpCircle, 
  Mail, 
  Phone, 
  ArrowLeft,
  Heart
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';

const PraktiskInformationPage = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    da: {
      seoTitle: "Praktisk Information - Handicapvenlige Rejser",
      seoDesc: "Få vigtig praktisk information og gode råd til din rejse med Alora Rejser. Læs om tilgængelighed, flyrejser, hjælpemidler og tryghed på rejsen.",
      back: "Tilbage",
      title: "Praktisk information",
      subtitle: "Her finder du vigtig information og gode råd til din rejse med Alora Rejser – før, under og efter din ferie.",
      
      beforeTitle: "Før du rejser",
      beforeText1: "Når du bestiller en rejse hos Alora Rejser, modtager du alle nødvendige oplysninger om fly, hotel og eventuelle transfers. Vi anbefaler, at du gennemgår dit rejsemateriale i god tid inden afrejse og sikrer dig, at dit pas er gyldigt, og at du har de nødvendige forsikringer og dokumenter med på rejsen.",
      beforeText2: "Har du behov for medicin, hjælpemidler eller særlige arrangementer under rejsen, anbefaler vi, at dette planlægges i god tid før afrejse.",
      
      accessTitle: "Tilgængelighed hos Alora Rejser",
      accessText1: "Hos Alora Rejser ved vi, at tilgængelighed betyder noget forskelligt fra person til person. Derfor lægger vi vægt på konkrete oplysninger, billeder og ærlige beskrivelser af hoteller og destinationer, så du kan vælge din ferie med større tryghed.",
      accessListTitle: "Vi udvælger rejser med fokus på blandt andet:",
      accessList: [
        "Handicapvenlige værelser og badeværelser",
        "Roll-in shower eller ekstra plads på badeværelset",
        "Poollift eller nem adgang til pool",
        "Fladt terræn og gode adgangsforhold",
        "Tilgængelige strande og strandhjælp, hvor det er muligt",
        "Hoteller med dokumenterede tilgængelighedsløsninger"
      ],
      accessText2: "Selvom vi gør os umage for at beskrive forholdene så præcist som muligt, kan individuelle behov variere. Har du særlige ønsker eller spørgsmål til et hotel eller en destination, hjælper vi gerne med at afklare forholdene inden bestilling.",
      
      flightTitle: "Fly og assistance",
      flightText1: "De fleste lufthavne tilbyder gratis assistance og du bestemmer selv, hvor meget hjælp du ønsker – nogle ønsker assistance hele vejen fra check-in til flysædet, mens andre kun har brug for hjælp ved ombordstigning eller ved ankomst.",
      flightText2: "Efter check-in bliver du mødt af lufthavnens assistanceteam, som hjælper dig gennem sikkerhedskontrollen og videre til gaten. I nogle lufthavne går man direkte ombord på flyet via en gatebro. Andre gange bliver man kørt ud til flyet i en liftbus (ambulift), som løfter kørestolsbrugere og andre passagerer med nedsat mobilitet op til flyets indgang uden brug af trapper.",
      flightText3: "Langt de fleste steder kan familie og rejsefæller følge med under hele forløbet, så I kan være sammen fra check-in og helt frem til flyet. Det giver ekstra tryghed, især hvis det er første gang, man rejser med assistance.",
      flightText4: "Bruger du egen kørestol, følger den som regel med helt ud til flyet og udleveres igen ved flyet eller i bagageområdet efter landing, afhængigt af lufthavnens procedurer. Har du el-kørestol eller andre hjælpemidler, skal oplysningerne blot oplyses inden afrejse, så flyselskabet kan forberede transporten.",
      flightText5: "Hos Alora Rejser hjælper vi gerne med vejledning om handicapassistance og de praktiske forhold omkring flyrejsen, så du kan rejse med ro i maven.",
      flightListTitle: "Godt at vide",
      flightList: [
        "Assistance i lufthavne er gratis",
        "Du vælger selv, hvor meget assistance du ønsker",
        "Familie og rejsefæller kan som regel følge med under hele assistancen",
        "Egen kørestol kan normalt medbringes uden ekstra gebyr",
        "Liftbus (ambulift) benyttes, når ombordstigning ikke kan ske direkte fra gaten",
        "El-kørestole og særlige hjælpemidler skal oplyses inden afrejse",
        "Personalet hjælper med check-in, sikkerhedskontrol og ombordstigning efter behov",
        "Du får normalt din kørestol tilbage ved flyet eller kort efter ankomst"
      ],
      flightTip: "Tip: Hvis du er usikker på, hvilken type assistance du skal bestille, hjælper vi gerne med at finde den løsning, der passer bedst til dine behov. Hos Alora Rejser ved vi, at små detaljer kan gøre en stor forskel for en tryg rejseoplevelse.",
      
      dayTitle: "På rejsedagen",
      dayText1: "Vi anbefaler, at du møder i lufthavnen i god tid før afgang. Har du bestilt assistance, skal du følge flyselskabets anvisninger om mødetid og check-in.",
      dayText2: "Sørg for at have pas, rejsepapirer og eventuelle oplysninger om hjælpemidler let tilgængelige, så rejsen starter så problemfrit som muligt.",
      
      arrivalTitle: "Ankomst til destinationen",
      arrivalText1: "Ved ankomst følger du informationerne i dit rejsemateriale vedrørende transfer eller transport til hotellet.",
      arrivalText2: "Har du bestilt transfer gennem Alora Rejser, vil oplysninger om mødested og transport fremgå af dine rejsedokumenter. Skulle du være i tvivl eller opleve problemer ved ankomsten, er du altid velkommen til at kontakte os.",
      
      keyTitle: "Check-in og check-out",
      keyText1: "Check-in på hotellet er normalt muligt fra sidst på eftermiddagen, mens værelset som regel skal forlades om formiddagen på afrejsedagen. De præcise tider fremgår af hotellets oplysninger.",
      keyText2: "Hvis du ankommer før værelset er klar, eller har sen hjemrejse, kan hotellet ofte opbevare bagagen, ligeledes er man ofte velkommen til at bruge hotellets faciliteter. Kontakt receptionen for nærmere information.",
      
      helpTitle: "Hjælp under rejsen",
      helpText1: "Hos Alora Rejser gør vi os umage for, at din rejse lever op til forventningerne. Skulle der mod forventning opstå problemer under rejsen, anbefaler vi, at du først kontakter hotellets reception eller den relevante leverandør på stedet. Kontakt derefter også os hurtigst muligt.",
      helpText2: "Jo tidligere vi bliver informeret, desto bedre mulighed har vi for at hjælpe og forsøge at finde en løsning, mens du stadig er afsted. Vi vil altid gøre vores bedste for at hjælpe hurtigst muligt.",
      
      contactTitle: "Kontakt",
      contactText: "Har du spørgsmål før, under eller efter din rejse, er du altid velkommen til at kontakte os.",
      contactFooter: "Vi glæder os til at hjælpe dig med din næste ferie – med ro i maven. 🌴♿"
    },
    en: {
      seoTitle: "Practical Information - Accessible Travel",
      seoDesc: "Get important practical information and good advice for your trip with Alora Rejser. Read about accessibility, flights, assistive devices, and peace of mind on your journey.",
      back: "Back",
      title: "Practical Information",
      subtitle: "Here you will find important information and good advice for your trip with Alora Rejser – before, during, and after your holiday.",
      
      beforeTitle: "Before you travel",
      beforeText1: "When you book a trip with Alora Rejser, you receive all necessary information about flights, hotel, and any transfers. We recommend that you review your travel documents well in advance of departure and ensure that your passport is valid and that you have the necessary insurance and documents with you.",
      beforeText2: "If you need medicine, assistive devices, or special arrangements during the trip, we recommend that this is planned well in advance of departure.",
      
      accessTitle: "Accessibility at Alora Rejser",
      accessText1: "At Alora Rejser, we know that accessibility means something different from person to person. Therefore, we emphasize concrete information, photos, and honest descriptions of hotels and destinations, so you can choose your holiday with greater peace of mind.",
      accessListTitle: "We select trips with a focus on, among other things:",
      accessList: [
        "Wheelchair-friendly rooms and bathrooms",
        "Roll-in shower or extra space in the bathroom",
        "Pool lift or easy access to the pool",
        "Flat terrain and good access conditions",
        "Accessible beaches and beach assistance where possible",
        "Hotels with documented accessibility solutions"
      ],
      accessText2: "Although we make every effort to describe conditions as accurately as possible, individual needs can vary. If you have special requests or questions about a hotel or destination, we are happy to help clarify the conditions before booking.",
      
      flightTitle: "Flight and assistance",
      flightText1: "Most airports offer free assistance, and you decide how much help you want – some want assistance all the way from check-in to the aircraft seat, while others only need help boarding or upon arrival.",
      flightText2: "After check-in, you will be met by the airport's assistance team, who will help you through security and on to the gate. In some airports, you board the plane directly via a jet bridge. Other times, you are driven to the plane in a lift bus (ambulift), which lifts wheelchair users and other passengers with reduced mobility up to the aircraft entrance without using stairs.",
      flightText3: "In the vast majority of places, family and travel companions can follow along during the entire process, so you can be together from check-in and all the way to the plane. This provides extra peace of mind, especially if it is the first time you travel with assistance.",
      flightText4: "If you use your own wheelchair, it usually goes with you all the way to the plane and is handed back at the plane or in the baggage area after landing, depending on airport procedures. If you have an electric wheelchair or other assistive devices, the information just needs to be provided before departure so the airline can prepare the transport.",
      flightText5: "At Alora Rejser, we are happy to help with guidance on disability assistance and the practicalities of the flight, so you can travel with peace of mind.",
      flightListTitle: "Good to know",
      flightList: [
        "Assistance in airports is free",
        "You choose how much assistance you want",
        "Family and travel companions can usually follow along during the entire assistance",
        "Your own wheelchair can normally be brought at no extra charge",
        "Lift bus (ambulift) is used when boarding cannot take place directly from the gate",
        "Electric wheelchairs and special assistive devices must be reported before departure",
        "Staff help with check-in, security control, and boarding as needed",
        "You normally get your wheelchair back at the plane or shortly after arrival"
      ],
      flightTip: "Tip: If you are unsure about what type of assistance to order, we are happy to help find the solution that best suits your needs. At Alora Rejser, we know that small details can make a big difference for a safe travel experience.",
      
      dayTitle: "On the day of travel",
      dayText1: "We recommend that you arrive at the airport well in advance of departure. If you have booked assistance, you must follow the airline's instructions regarding meeting time and check-in.",
      dayText2: "Make sure to have your passport, travel documents, and any information about assistive devices easily accessible, so the journey starts as smoothly as possible.",
      
      arrivalTitle: "Arrival at the destination",
      arrivalText1: "Upon arrival, follow the information in your travel materials regarding transfer or transport to the hotel.",
      arrivalText2: "If you have booked a transfer through Alora Rejser, information about the meeting point and transport will appear in your travel documents. Should you be in doubt or experience problems upon arrival, you are always welcome to contact us.",
      
      keyTitle: "Check-in and check-out",
      keyText1: "Check-in at the hotel is normally possible from late afternoon, while the room must usually be vacated in the morning on the day of departure. The exact times appear in the hotel's information.",
      keyText2: "If you arrive before the room is ready, or have a late departure, the hotel can often store your luggage, and you are also often welcome to use the hotel's facilities. Contact the reception for further information.",
      
      helpTitle: "Help during the trip",
      helpText1: "At Alora Rejser, we make every effort to ensure that your trip meets expectations. Should any problems, errors, or omissions arise during the trip, we recommend that you first contact the hotel's reception or the relevant supplier on site. Then contact us as soon as possible.",
      helpText2: "The earlier we are informed, the better opportunity we have to help and try to find a solution while you are still away. We will always do our best to help as quickly as possible.",
      
      contactTitle: "Contact",
      contactText: "If you have questions before, during, or after your trip, you are always welcome to contact us.",
      contactFooter: "We look forward to helping you with your next holiday – with peace of mind. 🌴♿"
    }
  };

  const t = content[language] || content.da;

  return (
    <div className="pt-24 pb-20 relative min-h-screen">
      <SEO 
        title={t.seoTitle} 
        description={t.seoDesc}
      />
      {/* Weak background image overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
        style={{
          backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      ></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Back button */}
        <Link 
          to={-1} 
          className="inline-flex items-center gap-2 text-[#99A18C] hover:text-[#8a917e] font-semibold mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t.back}
        </Link>

        {/* Header */}
        <div className="mb-12 text-center md:text-left">
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#99A18C] mb-4">
            {t.title}
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
            {t.subtitle}
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          
          {/* Før du rejser */}
          <motion.section 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white p-8 rounded-3xl border border-[#99A18C]/10 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#99A18C]/10 p-2 rounded-lg text-[#99A18C]">
                <FileText size={24} />
              </div>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-800">{t.beforeTitle}</h2>
            </div>
            <div className="text-slate-600 space-y-4 leading-relaxed">
              <p>{t.beforeText1}</p>
              <p>{t.beforeText2}</p>
            </div>
          </motion.section>

          {/* Tilgængelighed hos Alora Rejser */}
          <motion.section 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-white p-8 rounded-3xl border border-[#99A18C]/10 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#99A18C]/10 p-2 rounded-lg text-[#99A18C]">
                <span className="text-xl">♿</span>
              </div>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-800">{t.accessTitle}</h2>
            </div>
            <div className="text-slate-600 space-y-4 leading-relaxed">
              <p>{t.accessText1}</p>
              
              <div className="bg-stone-50 p-6 rounded-2xl border border-stone-100 my-6">
                <p className="font-semibold text-slate-800 mb-4">{t.accessListTitle}</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {t.accessList.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="text-[#99A18C] shrink-0 mt-0.5" size={16} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p>{t.accessText2}</p>
            </div>
          </motion.section>

          {/* Fly og assistance */}
          <motion.section 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="bg-white p-8 rounded-3xl border border-[#99A18C]/10 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#99A18C]/10 p-2 rounded-lg text-[#99A18C]">
                <Plane size={24} />
              </div>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-800">{t.flightTitle}</h2>
            </div>
            <div className="text-slate-600 space-y-4 leading-relaxed">
              <p>{t.flightText1}</p>
              <p>{t.flightText2}</p>
              <p>{t.flightText3}</p>
              <p>{t.flightText4}</p>
              <p>{t.flightText5}</p>

              <div className="bg-stone-50 p-6 rounded-2xl border border-stone-100 my-6">
                <p className="font-semibold text-slate-800 mb-4">{t.flightListTitle}</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {t.flightList.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="text-[#99A18C] shrink-0 mt-0.5" size={16} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#99A18C]/5 p-5 rounded-2xl border border-[#99A18C]/20 text-slate-700 italic text-sm">
                <span className="font-semibold text-[#99A18C] not-italic block mb-1">Tip:</span>
                {t.flightTip}
              </div>
            </div>
          </motion.section>

          {/* På rejsedagen */}
          <motion.section 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="bg-white p-8 rounded-3xl border border-[#99A18C]/10 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#99A18C]/10 p-2 rounded-lg text-[#99A18C]">
                <Calendar size={24} />
              </div>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-800">{t.dayTitle}</h2>
            </div>
            <div className="text-slate-600 space-y-4 leading-relaxed">
              <p>{t.dayText1}</p>
              <p>{t.dayText2}</p>
            </div>
          </motion.section>

          {/* Ankomst til destinationen */}
          <motion.section 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="bg-white p-8 rounded-3xl border border-[#99A18C]/10 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#99A18C]/10 p-2 rounded-lg text-[#99A18C]">
                <MapPin size={24} />
              </div>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-800">{t.arrivalTitle}</h2>
            </div>
            <div className="text-slate-600 space-y-4 leading-relaxed">
              <p>{t.arrivalText1}</p>
              <p>{t.arrivalText2}</p>
            </div>
          </motion.section>

          {/* Check-in og check-out */}
          <motion.section 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            className="bg-white p-8 rounded-3xl border border-[#99A18C]/10 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#99A18C]/10 p-2 rounded-lg text-[#99A18C]">
                <Key size={24} />
              </div>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-800">{t.keyTitle}</h2>
            </div>
            <div className="text-slate-600 space-y-4 leading-relaxed">
              <p>{t.keyText1}</p>
              <p>{t.keyText2}</p>
            </div>
          </motion.section>

          {/* Hjælp under rejsen */}
          <motion.section 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
            className="bg-white p-8 rounded-3xl border border-[#99A18C]/10 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#99A18C]/10 p-2 rounded-lg text-[#99A18C]">
                <HelpCircle size={24} />
              </div>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-800">{t.helpTitle}</h2>
            </div>
            <div className="text-slate-600 space-y-4 leading-relaxed">
              <p>{t.helpText1}</p>
              <p>{t.helpText2}</p>
            </div>
          </motion.section>

          {/* Kontakt */}
          <motion.section 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
            className="bg-[#99A18C]/10 p-8 rounded-3xl border-2 border-[#99A18C]/20 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#99A18C]/20 p-2 rounded-lg text-[#99A18C]">
                <Heart size={24} className="fill-[#99A18C]/20" />
              </div>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C]">{t.contactTitle}</h2>
            </div>
            <div className="text-slate-700 space-y-6 leading-relaxed">
              <p>{t.contactText}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <a 
                  href="mailto:info@alorarejser.dk" 
                  className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-[#99A18C]/10 hover:border-[#99A18C]/30 transition-colors"
                >
                  <Mail className="text-[#99A18C]" size={20} />
                  <div>
                    <p className="text-xs text-slate-500 font-bold uppercase">E-mail</p>
                    <p className="font-semibold text-slate-800">info@alorarejser.dk</p>
                  </div>
                </a>
                
                <a 
                  href="tel:+4540500780" 
                  className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-[#99A18C]/10 hover:border-[#99A18C]/30 transition-colors"
                >
                  <Phone className="text-[#99A18C]" size={20} />
                  <div>
                    <p className="text-xs text-slate-500 font-bold uppercase">{language === 'da' ? 'Telefon' : 'Phone'}</p>
                    <p className="font-semibold text-slate-800">+45 40 50 07 80</p>
                  </div>
                </a>
              </div>

              <p className="text-center font-serif italic text-lg text-[#99A18C] pt-6 border-t border-[#99A18C]/10">
                {t.contactFooter}
              </p>
            </div>
          </motion.section>

        </div>
      </div>
    </div>
  );
};

export default PraktiskInformationPage;
